import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  isDeferMachineBindingEnabled,
  isDeferRoleMaintenanceEnabled
} from '../infrastructure/startup/bootFeatureFlags';
import { runStartupMachineBinding } from '../services/startupMachineBinding';
import { kvGet, kvRemove } from '../infrastructure/storage/kvAccess';
import { markBootGate, measureBoot } from '../infrastructure/telemetry/bootTelemetry';
import {
  markRoleMaintenanceDone as signalRoleMaintenanceDone,
  resetStartupIntegritySignals,
  setMachineBindingState,
  subscribeRoleMaintenanceDone
} from '../infrastructure/startup/startupIntegritySignals';

const StartupIntegrityContext = createContext(null);

export function useStartupIntegrity() {
  const ctx = useContext(StartupIntegrityContext);
  if (!ctx) {
    return {
      machineBindingReady: true,
      machineBindingBlocked: false,
      roleMaintenanceDone: true,
      bindingReason: null,
      financialOpsReady: true,
      deferMachineBinding: false,
      deferRoleMaintenance: false
    };
  }
  return ctx;
}

function scheduleIdle(task, timeoutMs = 3000) {
  if (typeof requestIdleCallback === 'function') {
    requestIdleCallback(() => void task(), { timeout: timeoutMs });
  } else {
    setTimeout(() => void task(), 0);
  }
}

export function StartupIntegrityProvider({ children }) {
  const deferMachineBinding = isDeferMachineBindingEnabled();
  const deferRoleMaintenance = isDeferRoleMaintenanceEnabled();

  const [machineBindingReady, setMachineBindingReady] = useState(!deferMachineBinding);
  const [machineBindingBlocked, setMachineBindingBlocked] = useState(false);
  const [roleMaintenanceDone, setRoleMaintenanceDone] = useState(!deferRoleMaintenance);
  const [bindingReason, setBindingReason] = useState(null);

  const bindingStartedRef = useRef(false);

  const handleBindingFailure = useCallback((reason) => {
    setMachineBindingBlocked(true);
    setBindingReason(reason || null);
    setMachineBindingReady(false);
    alert(reason || 'Diese App ist an einen anderen Computer gebunden.');
    // Full session teardown, not just two keys — role, keep-alive and warm caches
    // have to go as well, and completeLogout emits authStateChanged(null) (RL03).
    void import('../services/completeLogoutService')
      .then(({ completeLogout }) => completeLogout({ reload: false }))
      .catch(() => {
        kvRemove('currentUser');
        kvRemove('currentBusiness');
        window.dispatchEvent(new CustomEvent('authStateChanged', { detail: { user: null } }));
      });
  }, []);

  const runDeferredMachineBinding = useCallback(async () => {
    if (!deferMachineBinding || bindingStartedRef.current) return;
    bindingStartedRef.current = true;
    markBootGate('machine_binding_start');

    const storedUserEarly = kvGet('currentUser');
    if (storedUserEarly?.type === 'admin') {
      setMachineBindingReady(true);
      setMachineBindingBlocked(false);
      setBindingReason(null);
      markBootGate('machine_binding_done', { canProceed: true, admin: true });
      return;
    }

    const result = await runStartupMachineBinding({ storedUserEarly });

    markBootGate('machine_binding_done', { canProceed: result.canProceed });
    measureBoot('machine_binding_start', 'machine_binding_done', 'machine_binding');

    if (!result.canProceed && result.blocked) {
      handleBindingFailure(result.reason);
      return;
    }

    setMachineBindingReady(true);
    setMachineBindingBlocked(false);
    setBindingReason(null);
  }, [deferMachineBinding, handleBindingFailure]);

  useEffect(() => {
    return subscribeRoleMaintenanceDone(setRoleMaintenanceDone);
  }, []);

  useEffect(() => {
    setMachineBindingState(machineBindingReady, machineBindingBlocked);
  }, [machineBindingReady, machineBindingBlocked]);

  useEffect(() => {
    if (!deferMachineBinding) return undefined;

    const maybeSchedule = () => {
      if (kvGet('currentUser')) {
        scheduleIdle(() => void runDeferredMachineBinding(), 2000);
      }
    };

    maybeSchedule();

    const onAuth = (event) => {
      if (event?.detail?.user) {
        // Admin dashboard session — skip machine binding (would logout on install mismatch)
        if (event.detail.user.type === 'admin') {
          setMachineBindingReady(true);
          setMachineBindingBlocked(false);
          setBindingReason(null);
          bindingStartedRef.current = true;
          return;
        }
        bindingStartedRef.current = false;
        scheduleIdle(() => void runDeferredMachineBinding(), 500);
      } else {
        setMachineBindingReady(!deferMachineBinding);
        setMachineBindingBlocked(false);
        bindingStartedRef.current = false;
        resetStartupIntegritySignals();
        setRoleMaintenanceDone(!deferRoleMaintenance);
      }
    };

    window.addEventListener('authStateChanged', onAuth);
    return () => window.removeEventListener('authStateChanged', onAuth);
  }, [deferMachineBinding, runDeferredMachineBinding]);

  const markRoleMaintenanceDone = useCallback(() => {
    signalRoleMaintenanceDone();
  }, []);

  const financialOpsReady =
    machineBindingReady && roleMaintenanceDone && !machineBindingBlocked;

  const value = useMemo(
    () => ({
      machineBindingReady,
      machineBindingBlocked,
      roleMaintenanceDone,
      bindingReason,
      financialOpsReady,
      deferMachineBinding,
      deferRoleMaintenance,
      markRoleMaintenanceDone,
      runDeferredMachineBinding
    }),
    [
      machineBindingReady,
      machineBindingBlocked,
      roleMaintenanceDone,
      bindingReason,
      financialOpsReady,
      deferMachineBinding,
      deferRoleMaintenance,
      markRoleMaintenanceDone,
      runDeferredMachineBinding
    ]
  );

  return (
    <StartupIntegrityContext.Provider value={value}>{children}</StartupIntegrityContext.Provider>
  );
}
